'use client'

import * as Dialog from '@radix-ui/react-dialog'
import { ExternalLink, FolderKanban, Gauge, Rocket, UserRound, UsersRound, X } from 'lucide-react'
import { createContext, useContext, useEffect, useMemo, useRef, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import { Badge } from './ui/badge.js'
import { Button } from './ui/button.js'
import { cn } from '../lib/utils.js'
import { getNounPreviews, type PortalNounPreviewRef } from '../lib/portal-api.js'

export type NounKind = 'project' | 'environment' | 'team' | 'member' | 'deployment'

export type NounPreview = {
  key: string
  kind: NounKind
  href: string
  title: string
  subtitle?: string | undefined
  status?: string | undefined
  description?: string | undefined
  metadata?: Array<{ label: string; value: string }> | undefined
}

function refForPreview(preview: NounPreview): PortalNounPreviewRef | null {
  const value = preview.key.slice(preview.key.indexOf(':') + 1)
  if (preview.kind === 'project') return { kind: 'project', slug: value }
  if (preview.kind === 'environment') { const [project_slug, env_slug] = value.split('/'); return project_slug && env_slug ? { kind: 'environment', project_slug, env_slug } : null }
  if (preview.kind === 'team' || preview.kind === 'member') return { kind: preview.kind, id: value }
  if (preview.kind === 'deployment') { const [project_slug, env_slug, deployment_id] = value.split('/'); return project_slug && env_slug && deployment_id ? { kind: 'deployment', project_slug, env_slug, deployment_id } : null }
  return null
}

export function prefetchNounPreviews(previews: NounPreview[]) {
  const refs = previews.flatMap((preview) => {
    const ref = refForPreview(preview)
    return ref ? [ref] : []
  })
  return refs.length === 0 ? Promise.resolve([]) : getNounPreviews(refs)
}

type PreviewContextValue = {
  open: (preview: NounPreview) => void
  close: () => void
  prefetch: (preview: NounPreview) => void
}

const PreviewContext = createContext<PreviewContextValue | null>(null)

function isPlainPrimaryClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey
}

function previewSearch(preview: NounPreview) {
  const params = new URLSearchParams()
  params.set('preview', preview.kind)
  params.set('previewId', preview.key)
  return params
}

function updatePreviewURL(preview: NounPreview | null, mode: 'push' | 'replace') {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (preview) {
    const params = previewSearch(preview)
    url.searchParams.set('preview', params.get('preview')!)
    url.searchParams.set('previewId', params.get('previewId')!)
  } else {
    url.searchParams.delete('preview')
    url.searchParams.delete('previewId')
  }
  window.history[mode === 'push' ? 'pushState' : 'replaceState']({}, '', `${url.pathname}${url.search}${url.hash}`)
}

/**
 * `prefetch` is deliberately injected by the route client. It keeps this UI
 * layer independent of the portal API wire format while allowing one
 * hover/focus micro-batch for refs whose projection was not in first paint.
 */
export function NounPreviewProvider({ previews, prefetch, children }: { previews?: NounPreview[]; prefetch?: (previews: NounPreview[]) => Promise<NounPreview[]>; children: ReactNode }) {
  const [warmed, setWarmed] = useState<NounPreview[]>([])
  const index = useMemo(() => new Map([...(previews ?? []), ...warmed].map((preview) => [preview.key, preview])), [previews, warmed])
  const [openPreview, setOpenPreview] = useState<NounPreview | null>(null)
  const pushedPreview = useRef(false)
  const queuedKeys = useMemo(() => new Set<string>(), [])
  const queuedFallbacks = useMemo(() => new Map<string, NounPreview>(), [])
  const [prefetchScheduled, setPrefetchScheduled] = useState(false)

  useEffect(() => {
    const restore = () => {
      const params = new URLSearchParams(window.location.search)
      const kind = params.get('preview') as NounKind | null
      const key = params.get('previewId')
      const found = key ? index.get(key) : undefined
      setOpenPreview(found && found.kind === kind ? found : null)
      pushedPreview.current = false
    }
    restore()
    window.addEventListener('popstate', restore)
    return () => window.removeEventListener('popstate', restore)
  }, [index])

  const value = useMemo<PreviewContextValue>(() => ({
    open(preview) {
      setOpenPreview(preview)
      pushedPreview.current = true
      updatePreviewURL(preview, 'push')
      // The compact reference opens immediately; replace it with the
      // authorized full projection as soon as the batch response arrives.
      void prefetch?.([preview]).then((resolved) => {
        const full = resolved.find((candidate) => candidate.key === preview.key)
        if (!full) return
        setWarmed((existing) => [...existing.filter((candidate) => candidate.key !== full.key), full])
        setOpenPreview((current) => current?.key === full.key ? full : current)
      }).catch(() => undefined)
    },
    close() {
      setOpenPreview(null)
      if (pushedPreview.current && typeof window !== 'undefined') {
        pushedPreview.current = false
        window.history.back()
      } else updatePreviewURL(null, 'replace')
    },
    prefetch(preview) {
      if (!prefetch || index.has(preview.key) || queuedKeys.has(preview.key)) return
      queuedKeys.add(preview.key)
      queuedFallbacks.set(preview.key, preview)
      if (!prefetchScheduled) setPrefetchScheduled(true)
    },
  }), [index, prefetch, prefetchScheduled, queuedFallbacks, queuedKeys])

  useEffect(() => {
    if (!prefetchScheduled || !prefetch) return
    const keys = [...queuedKeys]
    const pending = keys.map((key) => queuedFallbacks.get(key)).filter((preview): preview is NounPreview => preview !== undefined)
    queuedKeys.clear()
    setPrefetchScheduled(false)
    void prefetch(pending).then((resolved) => {
      setWarmed((existing) => [...existing, ...resolved])
    }).catch(() => {
      // Refs retain their already-rendered projection. A failed warm must not
      // create a loading blade or make normal navigation unavailable.
    }).finally(() => {
      keys.forEach((key) => queuedFallbacks.delete(key))
    })
  }, [prefetch, prefetchScheduled, queuedFallbacks, queuedKeys])

  return <PreviewContext.Provider value={value}>{children}<NounPreviewBlade preview={openPreview} onClose={value.close} /></PreviewContext.Provider>
}

export function NounPreviewBlade({ preview, onClose }: { preview: NounPreview | null; onClose: () => void }) {
  return <Dialog.Root open={preview !== null} onOpenChange={(next) => { if (!next) onClose() }}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[60] bg-[var(--ink)]/20 backdrop-blur-[1px]" />
      <Dialog.Content className="fixed inset-y-0 right-0 z-[60] flex w-full max-w-[42rem] flex-col overflow-y-auto border-l border-[var(--border)] bg-[var(--card)] shadow-2xl focus:outline-none">
        {preview ? <>
          <header className="flex items-start justify-between gap-4 border-b border-[var(--border)] px-6 py-5">
            <div className="min-w-0"><Dialog.Title className="truncate text-xl font-semibold">{preview.title}</Dialog.Title>{preview.subtitle ? <Dialog.Description className="mt-1 text-sm text-[var(--ink-muted)]">{preview.subtitle}</Dialog.Description> : null}</div>
            <Dialog.Close asChild><Button variant="ghost" size="icon" aria-label="Close preview"><X className="h-4 w-4" /></Button></Dialog.Close>
          </header>
          <div className="grid gap-5 px-6 py-6">
            <div className="flex flex-wrap items-center gap-2"><Badge>{preview.kind}</Badge>{preview.status ? <Badge>{preview.status}</Badge> : null}</div>
            {preview.description ? <p className="text-sm leading-6 text-[var(--ink-muted)]">{preview.description}</p> : null}
            {preview.metadata?.length ? <dl className="grid gap-3 rounded-lg border border-[var(--border)] p-4 text-sm">{preview.metadata.map((item) => <div key={item.label} className="grid gap-1"><dt className="text-xs font-medium uppercase tracking-wide text-[var(--ink-muted)]">{item.label}</dt><dd className="break-all">{item.value}</dd></div>)}</dl> : null}
            <div><a href={preview.href} className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] px-3 py-2 text-sm font-medium no-underline hover:bg-[var(--muted)]">Open full page <ExternalLink className="h-4 w-4" /></a></div>
          </div>
        </> : null}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
}

function NounRef({ preview, className, children, onClick, ...props }: { preview: NounPreview; children?: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const context = useContext(PreviewContext)
  const Icon = preview.kind === 'project' ? FolderKanban : preview.kind === 'environment' ? Gauge : preview.kind === 'team' ? UsersRound : preview.kind === 'member' ? UserRound : Rocket
  return <a {...props} href={preview.href} className={cn('inline-flex items-center gap-1.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]', className)} onClick={(event) => {
    onClick?.(event)
    if (event.defaultPrevented || !context || !isPlainPrimaryClick(event)) return
    event.preventDefault()
    context.open(preview)
  }} onMouseEnter={() => context?.prefetch(preview)} onFocus={() => context?.prefetch(preview)}><Icon className="h-3.5 w-3.5 shrink-0 text-[var(--accent)]" aria-hidden="true" />{children ?? preview.title}</a>
}

export function ProjectRef({ slug, name, description, className, children }: { slug: string; name: string; description?: string | undefined; className?: string | undefined; children?: ReactNode }) {
  return <NounRef preview={{ key: `project:${slug}`, kind: 'project', href: `/projects/${encodeURIComponent(slug)}`, title: name, description }} className={className}>{children}</NounRef>
}

export function EnvironmentRef({ projectSlug, slug, name, description, className, children }: { projectSlug: string; slug: string; name: string; description?: string | undefined; className?: string | undefined; children?: ReactNode }) {
  return <NounRef preview={{ key: `environment:${projectSlug}/${slug}`, kind: 'environment', href: `/projects/${encodeURIComponent(projectSlug)}/environments/${encodeURIComponent(slug)}`, title: name, subtitle: projectSlug, description }} className={className}>{children}</NounRef>
}

export function TeamRef({ id, name, description, className, children }: { id: string; name: string; description?: string | undefined; className?: string | undefined; children?: ReactNode }) {
  return <NounRef preview={{ key: `team:${id}`, kind: 'team', href: `/organization/teams/${encodeURIComponent(id)}`, title: name, description }} className={className}>{children}</NounRef>
}

export function MemberRef({ id, name, email, className, children }: { id: string; name: string; email?: string | undefined; className?: string | undefined; children?: ReactNode }) {
  return <NounRef preview={{ key: `member:${id}`, kind: 'member', href: `/members/${encodeURIComponent(id)}`, title: name, subtitle: email }} className={className}>{children}</NounRef>
}

export function DeploymentRef({ projectSlug, envSlug, deploymentID, runID, status, createdAt, className, children }: { projectSlug: string; envSlug: string; deploymentID: string; runID: string; status: string; createdAt?: string | undefined; className?: string | undefined; children?: ReactNode }) {
  return <NounRef preview={{ key: `deployment:${projectSlug}/${envSlug}/${deploymentID}`, kind: 'deployment', href: `/projects/${encodeURIComponent(projectSlug)}/environments/${encodeURIComponent(envSlug)}/deployments/${encodeURIComponent(deploymentID)}`, title: runID, subtitle: `${projectSlug} / ${envSlug}`, status, metadata: createdAt ? [{ label: 'Started', value: createdAt }] : undefined }} className={className}>{children}</NounRef>
}
